import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const STORAGE_KEY = 'xsite_auth';

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.token ? parsed : null;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  // bootstrapping is true on first render until we've decided whether the
  // stored token is valid — so ProtectedRoute / Login don't bounce users
  // back to /login while the check is in flight.
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    const stored = readStored();
    if (!stored) {
      setBootstrapping(false);
      return;
    }
    axios.defaults.headers.common.Authorization = `Bearer ${stored.token}`;
    // Verify with the existing /api/auth/me endpoint. If the token is
    // valid we restore the session; if it's expired we wipe it.
    axios.get('/api/auth/me')
      .then((res) => {
        setToken(stored.token);
        setUser(res.data?.user || stored.user || null);
      })
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
        delete axios.defaults.headers.common.Authorization;
      })
      .finally(() => setBootstrapping(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await axios.post('/api/auth/login', { email, password });
    const { token: newToken, user: newUser } = res.data;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token: newToken, user: newUser }));
    axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(newUser);
    return newUser;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    delete axios.defaults.headers.common.Authorization;
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ token, user, isAuthenticated: !!token, bootstrapping, login, logout }),
    [token, user, bootstrapping, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
